import React, { useState, useEffect } from 'react';
import { API_URL } from '../config/api';


const AdminManageCourts = () => {
  const [courts, setCourts] = useState([]);
  const [sports, setSports] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editMode, setEditMode] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [courtStats, setCourtStats] = useState({});
  
  const [formData, setFormData] = useState({
    name: '',
    court_number: '',
    sport_id: '',
    facility_id: '',
    capacity: '4',
    description: '',
    equipment_included: '',
    features: '',
    is_active: true
  });

  const token = localStorage.getItem('adminToken');
  console.log('AdminManageCourts: token exists?', !!token, token ? token.substring(0, 20) + '...' : 'null');

  useEffect(() => {
    console.log('AdminManageCourts useEffect running, token:', !!token);
    // Only fetch if token exists
    if (token) {
      console.log('Token found, calling fetchCourts...');
      fetchCourts();
      fetchSports();
      fetchFacilities();
    } else {
      console.log('No token, skipping fetch');
    }
  }, [token]);

  const fetchCourts = async () => {
    if (!token) {
      setError('Authentication required');
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/courts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.status === 403) {
        setError('Access denied. Please check your admin permissions.');
        return;
      }

      const data = await response.json();
      console.log('Courts API response:', data);
      if (data.success) {
        console.log('Setting courts:', data.courts?.length || 0, 'courts');
        setCourts(data.courts || []);
        setError('');
      } else {
        console.log('API returned success: false', data.message);
        setError(data.message || 'Failed to load courts');
      }
    } catch (err) {
      console.error('Error fetching courts:', err);
      setError('Failed to load courts');
    } finally {
      setLoading(false);
    }
  };

  const fetchSports = async () => {
    try {
      const response = await fetch(`${API_URL}/sports`);
      const data = await response.json();
      if (Array.isArray(data)) setSports(data);
    } catch (err) {
      console.error('Error fetching sports:', err);
    }
  };

  const fetchFacilities = async () => {
    try {
      const response = await fetch(`${API_URL}/facilities`);
      const data = await response.json();
      if (Array.isArray(data)) setFacilities(data);
    } catch (err) {
      console.error('Error fetching facilities:', err);
    }
  };

  const fetchCourtStats = async (courtId) => {
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/admin/courts/${courtId}/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setCourtStats(prev => ({ ...prev, [courtId]: data.stats }));
      }
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      court_number: '',
      sport_id: '',
      facility_id: '',
      capacity: '4',
      description: '',
      equipment_included: '',
      features: '',
      is_active: true
    });
    setEditMode(null);
    setShowAddForm(false);
    setCourtStats({});
  };

  const handleEdit = (court) => {
    setEditMode(court.id);
    setFormData({
      name: court.name,
      court_number: court.court_number || '',
      sport_id: court.sport_id,
      facility_id: court.facility_id,
      capacity: court.capacity || '4',
      description: court.description || '',
      equipment_included: Array.isArray(court.equipment_included) 
        ? court.equipment_included.join(', ') 
        : '',
      features: Array.isArray(court.features) 
        ? court.features.join(', ') 
        : '',
      is_active: court.is_active
    });
    setShowAddForm(true);
    fetchCourtStats(court.id);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!token) {
      setError('Authentication required');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const url = editMode
        ? `${API_URL}/admin/courts/${editMode}`
        : `${API_URL}/admin/courts`;
      
      const method = editMode ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccess(data.message);
        fetchCourts();
        setTimeout(() => {
          resetForm();
          setSuccess('');
        }, 2000);
      } else {
        setError(data.message || 'Failed to save court');
      }
    } catch (err) {
      setError('Failed to save court: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (courtId) => {
    if (!token) {
      setError('Authentication required');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/admin/courts/${courtId}/toggle-active`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccess(data.message);
        fetchCourts();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.message || 'Failed to update status');
      }
    } catch (err) {
      setError('Failed to update status');
    }
  };

  const handleDelete = async (courtId) => {
    if (!window.confirm('Are you sure you want to delete this court? This cannot be undone.')) {
      return;
    }

    if (!token) {
      setError('Authentication required');
      return;
    }

    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/admin/courts/${courtId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccess(data.message);
        fetchCourts();
      } else {
        setError(data.message || 'Failed to delete court');
      }
    } catch (err) {
      setError('Failed to delete court');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="admin-loading">
        <div className="admin-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="admin-btn admin-btn-primary"
        >
          {showAddForm ? 'Cancel' : '+ Add Court'}
        </button>
      </div>

      {success && <div className="admin-alert success">{success}</div>}
      {error && <div className="admin-alert error">{error}</div>}

      {showAddForm && (
        <div className="admin-card" style={{ marginBottom: '16px' }}>
          <div className="admin-card-header">{editMode ? 'Edit Court' : 'Add New Court'}</div>
          <div className="admin-card-body">
            {editMode && courtStats[editMode] && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '20px', padding: '16px', background: '#f9fafb', borderRadius: '8px' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: 700, color: '#667eea' }}>{courtStats[editMode].total_bookings}</div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>Total Bookings</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: 700, color: '#10b981' }}>{courtStats[editMode].confirmed_bookings}</div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>Confirmed</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: 700, color: '#f59e0b' }}>{courtStats[editMode].upcoming_bookings}</div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>Upcoming</div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="admin-form-row">
                <div className="admin-field">
                  <label className="admin-label">Court Name *</label>
                  <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required placeholder="e.g., Center Court" className="admin-input" />
                </div>
                <div className="admin-field">
                  <label className="admin-label">Court Number</label>
                  <input type="text" value={formData.court_number} onChange={(e) => setFormData({ ...formData, court_number: e.target.value })} placeholder="e.g., 1, A" className="admin-input" />
                </div>
                <div className="admin-field">
                  <label className="admin-label">Sport *</label>
                  <select value={formData.sport_id} onChange={(e) => setFormData({ ...formData, sport_id: e.target.value })} required className="admin-select">
                    <option value="">Select Sport</option>
                    {sports.map(sport => (<option key={sport.id} value={sport.id}>{sport.name}</option>))}
                  </select>
                </div>
                <div className="admin-field">
                  <label className="admin-label">Facility *</label>
                  <select value={formData.facility_id} onChange={(e) => setFormData({ ...formData, facility_id: e.target.value })} required className="admin-select">
                    <option value="">Select Facility</option>
                    {facilities.map(facility => (<option key={facility.id} value={facility.id}>{facility.name}</option>))}
                  </select>
                </div>
                <div className="admin-field">
                  <label className="admin-label">Capacity</label>
                  <input type="number" value={formData.capacity} onChange={(e) => setFormData({ ...formData, capacity: e.target.value })} min="1" className="admin-input" />
                </div>
                <div className="admin-field">
                  <label className="admin-label">Status</label>
                  <select value={formData.is_active ? 'active' : 'inactive'} onChange={(e) => setFormData({ ...formData, is_active: e.target.value === 'active' })} className="admin-select">
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>

              <div style={{ marginTop: '16px' }}>
                <div className="admin-field" style={{ marginBottom: '12px' }}>
                  <label className="admin-label">Description</label>
                  <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows="2" placeholder="Additional info..." className="admin-input" style={{ resize: 'vertical' }} />
                </div>
                <div className="admin-field" style={{ marginBottom: '12px' }}>
                  <label className="admin-label">Equipment (comma separated)</label>
                  <input type="text" value={formData.equipment_included} onChange={(e) => setFormData({ ...formData, equipment_included: e.target.value })} placeholder="Net, Balls..." className="admin-input" />
                </div>
                <div className="admin-field">
                  <label className="admin-label">Features (comma separated)</label>
                  <input type="text" value={formData.features} onChange={(e) => setFormData({ ...formData, features: e.target.value })} placeholder="AC, Lighting..." className="admin-input" />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '8px', marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
                <button type="button" onClick={resetForm} className="admin-btn admin-btn-secondary">Cancel</button>
                <button type="submit" disabled={loading} className="admin-btn admin-btn-primary">{loading ? 'Saving...' : editMode ? 'Update Court' : 'Add Court'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="admin-card">
        <div className="admin-card-header">Courts ({courts.length})</div>
        {courts.length === 0 && !loading ? (
          <div className="admin-empty">
            <div className="admin-empty-icon">No courts</div>
            <p>Click "Add Court" to create your first court</p>
          </div>
        ) : (
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Court Name</th>
                  <th>Number</th>
                  <th>Sport</th>
                  <th>Facility</th>
                  <th>Capacity</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {courts.map(court => (
                  <tr key={court.id}>
                    <td style={{ fontWeight: 600 }}>{court.name}</td>
                    <td>{court.court_number || '-'}</td>
                    <td>{court.sport_name}</td>
                    <td>{court.facility_name}</td>
                    <td>{court.capacity}</td>
                    <td>
                      <span className={`admin-badge ${court.is_active ? 'admin-badge-success' : 'admin-badge-danger'}`}>
                        {court.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button onClick={() => handleEdit(court)} className="admin-btn admin-btn-primary admin-btn-sm">Edit</button>
                        <button onClick={() => handleToggleActive(court.id)} className="admin-btn admin-btn-secondary admin-btn-sm">{court.is_active ? 'Disable' : 'Enable'}</button>
                        <button onClick={() => handleDelete(court.id)} className="admin-btn admin-btn-danger admin-btn-sm">Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminManageCourts;